/**
 * useJobOffersSync — Wrapper de useJobOffers avec synchronisation serveur.
 *
 * Offline-first : toutes les mutations écrivent d'abord dans localStorage,
 * puis synchro avec le serveur en arrière-plan (debounce 2s).
 *
 * Au login : charge les offres depuis le serveur et les fusionne avec le local
 * (union par id — garde le plus récent par updatedAt).
 */

import { useCallback, useEffect, useRef } from 'react';
import { useJobOffers } from './useJobOffers';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, apiFetchGet, apiFetchDelete } from '../lib/api-fetch';
import {
  getJobOffers,
  getCVxJobEntries,
  type JobOffer,
} from '../lib/job-offers-store';

export function useJobOffersSync() {
  const jobOffersHook = useJobOffers();
  const { session } = useAuth();
  const hasSyncedOnLoginRef = useRef(false);
  const syncDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Helper : push toutes les offres locales vers le serveur ──────────────
  const pushOffersToServer = useCallback(async () => {
    if (!session?.access_token) return;
    const offers = getJobOffers();
    if (!offers.length) return;
    try {
      await apiFetch('/api/user/job-offers', {
        offers: offers.map((o) => ({
          id: o.id,
          title: o.title,
          company: o.company,
          source_url: o.sourceUrl ?? null,
          source: o.source,
          raw_text: o.rawText,
          parsed_data: o.parsedData ?? null,
          is_favorite: o.isFavorite,
          date_added: o.dateAdded,
          updated_at: o.updatedAt ?? o.dateAdded,
        })),
      });
    } catch (e) {
      console.error('[useJobOffersSync] pushOffersToServer error:', e);
    }
  }, [session]);

  // ── Helper : push tous les CVxJob locaux vers le serveur ─────────────────
  const pushCVxJobToServer = useCallback(async () => {
    if (!session?.access_token) return;
    const entries = getCVxJobEntries();
    for (const entry of entries) {
      try {
        await apiFetch('/api/user/cvxjob', {
          id: entry.id,
          cv_version_id: entry.cvVersionId,
          job_offer_id: entry.jobOfferId,
          ats_score: entry.atsScore ?? null,
          adapted_cv_id: entry.adaptedCvId ?? null,
          created_at: entry.createdAt,
        });
      } catch { /* ignore — best effort */ }
    }
  }, [session]);

  // ── Planifier un push debounced ──────────────────────────────────────────
  const schedulePush = useCallback(() => {
    if (!session?.access_token) return;
    if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
    syncDebounceRef.current = setTimeout(() => {
      // Séquentiels : CVxJob a une FK → job_offers, donc les offres doivent
      // être persistées avant de pousser les entrées CVxJob.
      void (async () => {
        await pushOffersToServer();
        await pushCVxJobToServer();
      })();
    }, 2000);
  }, [session, pushOffersToServer, pushCVxJobToServer]);

  // ── Au login : charger depuis le serveur et fusionner ────────────────────
  useEffect(() => {
    if (!session?.access_token) {
      hasSyncedOnLoginRef.current = false;
      return;
    }
    if (hasSyncedOnLoginRef.current) return;
    hasSyncedOnLoginRef.current = true;

    async function syncOnLogin() {
      try {
        const [offersRes, cvxjobRes] = await Promise.all([
          apiFetchGet<{ jobOffers: Array<Record<string, unknown>> }>('/api/user/job-offers'),
          apiFetchGet<{ entries: Array<Record<string, unknown>> }>('/api/user/cvxjob'),
        ]);

        const serverOffers = offersRes.json.jobOffers ?? [];

        if (serverOffers.length > 0) {
          const localOffers = getJobOffers();
          const localById = new Map(localOffers.map((o) => [o.id, o]));

          for (const so of serverOffers) {
            const id = so.id as string;
            const local = localById.get(id);
            const serverUpdatedAt = String(so.updated_at ?? so.date_added ?? '');
            const localUpdatedAt = local?.updatedAt ?? local?.dateAdded ?? '';

            if (!local || serverUpdatedAt > localUpdatedAt) {
              localById.set(id, {
                id,
                title: String(so.title ?? ''),
                company: String(so.company ?? ''),
                sourceUrl: (so.source_url ?? undefined) as string | undefined,
                source: (so.source ?? 'manual') as JobOffer['source'],
                rawText: String(so.raw_text ?? ''),
                parsedData: so.parsed_data as JobOffer['parsedData'],
                isFavorite: Boolean(so.is_favorite),
                dateAdded: String(so.date_added ?? new Date().toISOString()),
                updatedAt: serverUpdatedAt,
              });
            }
          }

          const merged = Array.from(localById.values()).sort(
            (a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime(),
          );
          try { localStorage.setItem('cv_job_offers', JSON.stringify(merged)); } catch { /* ignore */ }
        } else {
          await pushOffersToServer();
        }

        // CVxJob : union simple
        const serverEntries = cvxjobRes.json.entries ?? [];
        if (serverEntries.length > 0) {
          const localEntries = getCVxJobEntries();
          const localById = new Map(localEntries.map((e) => [e.id, e]));
          for (const se of serverEntries) {
            const id = se.id as string;
            if (!localById.has(id)) {
              localById.set(id, {
                id,
                cvVersionId: String(se.cv_version_id ?? ''),
                jobOfferId: String(se.job_offer_id ?? ''),
                atsScore: (se.ats_score ?? undefined) as number | undefined,
                adaptedCvId: (se.adapted_cv_id ?? undefined) as string | undefined,
                createdAt: String(se.created_at ?? new Date().toISOString()),
              });
            }
          }
          try { localStorage.setItem('cv_xjob_entries', JSON.stringify(Array.from(localById.values()))); } catch { /* ignore */ }
        } else {
          await pushCVxJobToServer();
        }

        // Déclencher un re-render du hook
        jobOffersHook.refresh();
      } catch (e) {
        console.error('[useJobOffersSync] syncOnLogin error:', e);
      }
    }

    void syncOnLogin();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]);

  // ── Wrappers des mutations pour déclencher le sync ───────────────────────
  const addOffer = useCallback<typeof jobOffersHook.addOffer>(
    (offer) => {
      const result = jobOffersHook.addOffer(offer);
      schedulePush();
      return result;
    },
    [jobOffersHook, schedulePush],
  );

  const removeOffer = useCallback<typeof jobOffersHook.removeOffer>(
    (id) => {
      jobOffersHook.removeOffer(id);
      schedulePush();
      // Supprimer côté serveur (best effort)
      if (session?.access_token) {
        apiFetchDelete(`/api/user/job-offers?id=${encodeURIComponent(id)}`).catch(() => { /* ignore */ });
      }
    },
    [jobOffersHook, schedulePush, session],
  );

  const updateOffer = useCallback<typeof jobOffersHook.updateOffer>(
    (id, updates) => {
      jobOffersHook.updateOffer(id, updates);
      schedulePush();
    },
    [jobOffersHook, schedulePush],
  );

  const recordAnalysis = useCallback<typeof jobOffersHook.recordAnalysis>(
    (cvVersionId, jobOfferId, atsScore) => {
      jobOffersHook.recordAnalysis(cvVersionId, jobOfferId, atsScore);
      schedulePush();
    },
    [jobOffersHook, schedulePush],
  );

  const recordAdaptation = useCallback<typeof jobOffersHook.recordAdaptation>(
    (cvVersionId, jobOfferId, adaptedCvId) => {
      jobOffersHook.recordAdaptation(cvVersionId, jobOfferId, adaptedCvId);
      schedulePush();
    },
    [jobOffersHook, schedulePush],
  );

  return {
    ...jobOffersHook,
    addOffer,
    removeOffer,
    updateOffer,
    recordAnalysis,
    recordAdaptation,
  };
}
